import { useEffect } from 'react';

/* Site-wide pointer interactions, implemented with event delegation so every
   page and component gets them without per-page code:
   - 3D tilt + depth shadow on cards
   - magnetic buttons
   - soft cursor-follow glow                                                   */

const CARD_RE = /(^|[-_])card$|^card-brand$|-block$|-tile$|-box$|^tech-item$|^process-node$|^process-step$|^faq-item$|^why-us__feature$|^service-article$/;
const BTN_SEL = '.btn-primary-brand, .btn-outline-brand, .btn-outline-light';

function findCard(start) {
  let el = start;
  for (let i = 0; el && el !== document.body && i < 5; i += 1, el = el.parentElement) {
    if (el.classList && !el.closest('.qd, .fx-tilt, .site-header, .site-footer') && el.closest('main')) {
      for (const c of el.classList) if (CARD_RE.test(c)) return el;
    }
  }
  return null;
}

export function useInteractions(glowRef) {
  useEffect(() => {
    const fine = window.matchMedia?.('(hover: hover) and (pointer: fine)').matches;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!fine || reduced) return undefined;

    let card = null;
    let btn = null;
    let glowRaf = 0;
    let gx = -400;
    let gy = -400;

    const resetCard = (el) => {
      if (!el) return;
      el.style.transform = '';
      el.style.boxShadow = '';
    };
    const resetBtn = (el) => { if (el) el.style.transform = ''; };

    const onMove = (e) => {
      /* cursor glow */
      gx = e.clientX; gy = e.clientY;
      if (!glowRaf && glowRef.current) {
        glowRaf = requestAnimationFrame(() => {
          glowRaf = 0;
          if (glowRef.current) glowRef.current.style.transform = `translate3d(${gx - 250}px, ${gy - 250}px, 0)`;
        });
      }

      /* card tilt */
      const c = findCard(e.target);
      if (c !== card) { resetCard(card); card = c; if (card) card.classList.add('gx-card'); }
      if (card) {
        const r = card.getBoundingClientRect();
        if (r.height > 90 && r.width < window.innerWidth * 0.9) {
          const x = (e.clientX - r.left) / r.width;
          const y = (e.clientY - r.top) / r.height;
          const ry = (x - 0.5) * 7;
          const rx = (0.5 - y) * 7;
          card.style.transform = `perspective(1000px) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg) translateY(-4px)`;
          card.style.boxShadow = `${(-ry * 3).toFixed(1)}px ${(18 + rx * 3).toFixed(1)}px 50px -18px rgba(79,70,229,0.6)`;
        }
      }

      /* magnetic buttons */
      const b = e.target.closest?.(BTN_SEL);
      if (b !== btn) { resetBtn(btn); btn = b; if (btn) btn.classList.add('gx-btn'); }
      if (btn) {
        const r = btn.getBoundingClientRect();
        const dx = (e.clientX - (r.left + r.width / 2)) * 0.16;
        const dy = (e.clientY - (r.top + r.height / 2)) * 0.22;
        btn.style.transform = `translate(${dx.toFixed(1)}px, ${dy.toFixed(1)}px)`;
      }
    };

    const onLeaveDoc = () => { resetCard(card); resetBtn(btn); card = null; btn = null; };

    document.addEventListener('pointermove', onMove, { passive: true });
    document.documentElement.addEventListener('pointerleave', onLeaveDoc);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.documentElement.removeEventListener('pointerleave', onLeaveDoc);
      if (glowRaf) cancelAnimationFrame(glowRaf);
      resetCard(card); resetBtn(btn);
    };
  }, [glowRef]);
}
